import { ReactElement, useContext, useEffect, useState } from "react";
import "./index.scss";
import { ProjectContext } from "context/ProjectProvider";
import { LoadingOutlined } from "@ant-design/icons";
import { DateUtils } from "utils/dateutils";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import { FEATURE_ACTIVITY_BY_PK } from "services/graphQL/queries";
import ActivityOffsetTag from "components/activity-offset-tag";
import CriticalActivityTag from "pages/schedule/schedule-page/critical-activity-tag";
import OffsetIcon from "components/svg-icons/offset-icon";
import { TGoverningSource } from "entity-app/shared-components/entity-dateblock/models";

type TActivityTask = {
  activity_id: string;
  start_date: string;
  end_date: string;
  actual_start_date: string;
  actual_finish_date: string;
  critical: boolean;
  is_critical: boolean;
  total_slack: number | null;
  type: number;
  progress: number;
  duration: number;
  task_name: string;
  reverse_dependency: boolean;
  linked_to_end_date?: boolean;
  total_slack_computed?: any;
  offset_duration?: any;
  linked_offset_date?: any;
  feature_instance: {
    title: string;
    number: string;
  } | null;
};

function FeatureEntityTaskDetailCard({
  governingSource
}: {
  governingSource: TGoverningSource;
}): ReactElement {
  const [task, setTask] = useState<TActivityTask>();
  const { gqlClientForProject } = useContext(ProjectContext);

  const { data, loading } = useCIQQuery<{
    feature_activities_by_pk: TActivityTask;
  }>(FEATURE_ACTIVITY_BY_PK, {
    client: gqlClientForProject,
    variables: {
      id: governingSource.id
    },
    skip: !governingSource.id
  });

  useEffect(() => {
    if (data?.feature_activities_by_pk) {
      setTask(data.feature_activities_by_pk);
    }
  }, [data]);

  if (loading || !task) {
    return (
      <div className="db-task-detail-card">
        <div className="h-full w-full flex items-center justify-center">
          <LoadingOutlined />
        </div>
      </div>
    );
  }

  function linkedActivitiesTooltip(props: { activity: TActivityTask }) {
    const { activity } = props;
    const featureInstance = activity.feature_instance;
    const name = featureInstance?.number
      ? `${featureInstance.number} - ${featureInstance.title}`
      : (featureInstance?.title ?? governingSource.name);

    const getLabels = (
      label: string,
      value: string,
      valueClass: string = ""
    ) => {
      return (
        <div className="grid grid-cols-9 text-[10px] text-ciq-gray-700">
          <div className="col-span-4 font-medium">{label}</div>
          <div
            className={`flex items-center space-x-1 col-span-5 text-black text-opacity-80 ${valueClass}`}
          >
            <span>{value}</span>
            {governingSource?.linkedOffsetDate &&
              (label === "Planned Start date:" ||
                label === "Planned End date:") && (
                <ActivityOffsetTag
                  task={{
                    offset_duration: governingSource.offsetDuration,
                    linked_offset_date: governingSource.linkedOffsetDate
                  }}
                  isShowDate
                />
              )}
          </div>
        </div>
      );
    };
    const isPermissionEnabled = activity?.total_slack_computed !== "restricted";
    const duration =
      governingSource?.offsetDuration < 0
        ? governingSource?.offsetDuration
        : `+${governingSource?.offsetDuration}`;
    const popupContent = (
      <div className="min-w-[228px] max-w-[299px] p-1 ">
        {isPermissionEnabled && activity.critical && <CriticalActivityTag />}
        <div className="text-xs font-medium mt-2 mb-2">{name}</div>
        {governingSource?.isLinkedEndDate
          ? getLabels(
              "Planned End date:",
              DateUtils.format(activity?.end_date, "MM-DD-YYYY")
            )
          : getLabels(
              "Planned Start date:",
              DateUtils.format(activity?.start_date, "MM-DD-YYYY")
            )}
        {governingSource.offsetDuration && (
          <div className="mt-2">
            {getLabels("Link Properties:", "")}
            <div className="flex items-center justify-center bg-ciq-gray-100 rounded h-5 space-x-1 px-2">
              <span className="flex items-center">
                <OffsetIcon />
              </span>
              <span className="flex items-center font-normal text-xs">
                Offset: {duration} Days
              </span>
            </div>
          </div>
        )}
      </div>
    );

    return popupContent;
  }

  return <div>{linkedActivitiesTooltip({ activity: task })}</div>;
}

export default FeatureEntityTaskDetailCard;
